import { useCallback, useState } from 'react'
import { Network, Wifi, Globe } from 'lucide-react'
import AdminScreenShell, { Tegel, StatusRij } from '@/components/AdminScreenShell'
import { networkAPI, adminAPI } from '@/lib/api'

export default function AdminNetworkScreen() {
  const [info, setInfo]     = useState(null)
  const [ping, setPing]     = useState(null)
  const [funnel, setFunnel] = useState(null)
  const [err, setErr]       = useState(null)

  const load = useCallback(async () => {
    try {
      const [i, p, f] = await Promise.all([
        networkAPI.getNetworkInfo().catch(() => ({ data: null })),
        networkAPI.ping().catch(() => ({ data: null })),
        adminAPI.getFunnelStatus().catch(() => ({ data: null })),
      ])
      setInfo(i.data ?? null)
      setPing(p.data ?? null)
      setFunnel(f.data ?? null)
      setErr(null)
    } catch (e) { setErr(e.message) }
  }, [])

  const interfaces = info?.interfaces ?? []

  return (
    <AdminScreenShell title="Netwerk" subtitle="Interfaces · Tailscale · connectiviteit"
                      icon={Network} accent="#337cff" onRefresh={load} refreshMs={15_000}>
      {err && <div className="text-signal text-sm mb-4">{err}</div>}

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-6">
        <Tegel label="Gateway" waarde={info?.gateway || '—'} sub="LAN-router" kleur="#337cff" />
        <Tegel label="DNS" waarde={info?.dns?.[0] || '—'}
               sub={info?.dns?.length > 1 ? `+${info.dns.length - 1} meer` : ''} kleur="#00d9ff" />
        <Tegel label="Ping" waarde={ping?.ok ? `${ping.rtt_ms ?? '—'} ms` : 'geen'}
               sub={ping?.target || 'router'} kleur={ping?.ok ? '#61E294' : '#FF5A5A'} />
        <Tegel label="Funnel" waarde={funnel?.active ? 'aan' : 'uit'}
               sub={funnel?.url ? new URL(funnel.url).host : '—'}
               kleur={funnel?.active ? '#B4FF39' : '#94A3B8'} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Wifi className="inline h-3 w-3 mr-1" /> Interfaces
          </h2>
          <div className="rounded-[6px] border border-border bg-card px-4">
            {interfaces.length === 0 && <p className="py-3 text-sm text-muted-foreground">Geen data.</p>}
            {interfaces.map(i => (
              <StatusRij key={i.name} naam={i.name}
                         sub={`${i.ipv4 || i.address || '—'}${i.type ? ` · ${i.type}` : ''}`}
                         status={i.up === false ? 'down' : 'up'}
                         kleur={i.up === false ? '#94A3B8' : '#61E294'} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Globe className="inline h-3 w-3 mr-1" /> Tailscale / Funnel
          </h2>
          <div className="rounded-[6px] border border-border bg-card p-4 space-y-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Tailscale IP</div>
              <div className="text-xl font-semibold tabular-nums">{info?.tailscale?.ip || '—'}</div>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Publieke URL</div>
              <div className="text-lg font-mono break-all">{funnel?.url || 'niet publiek'}</div>
            </div>
          </div>
        </section>
      </div>
    </AdminScreenShell>
  )
}
